import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';

/**
 * Reject anything that is not an absolute https: destination.
 *
 * `window.location.href = <backend value>` is a navigation primitive, so a
 * `javascript:` or `data:` string there executes rather than navigates. Stripe
 * only ever returns https, which makes this free in the normal case and the
 * difference between a bug and a redirect gadget in the abnormal one.
 * `src/lib/billing-portal.ts` already holds this line for the same class of
 * backend-supplied URL; this keeps the two consistent.
 */
function safeCheckoutDestination(url: string): string | null {
  try {
    const destination = new URL(url);
    return destination.protocol === 'https:' ? destination.href : null;
  } catch {
    return null;
  }
}

/**
 * Send the browser to Stripe checkout from a BE checkout-session response.
 *
 * Handles both response shapes: a hosted-page `url` (plain redirect) and a
 * `sessionId` (stripe.js `redirectToCheckout`). Returns false after showing a
 * toast when no redirect happened, so callers can clear their in-flight state.
 * The supplied fence prevents an awaited Stripe load from redirecting after an
 * organization switch, sign-out, or unmount.
 */
export async function redirectToStripeCheckout(
  response: {
    url?: string;
    sessionId?: string;
  },
  isActionCurrent: () => boolean
): Promise<boolean> {
  if (!isActionCurrent()) return true;
  if (response.url) {
    const destination = safeCheckoutDestination(response.url);
    if (!destination) {
      toast.error('Checkout returned an unsafe destination');
      return false;
    }
    if (!isActionCurrent()) return true;
    window.location.href = destination;
    return true;
  }
  if (response.sessionId) {
    const stripe = await loadStripe(
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
    );
    if (!isActionCurrent()) return true;
    if (!stripe) {
      toast.error('Payment system unavailable. Please try again.');
      return false;
    }
    if (!isActionCurrent()) return true;
    const { error } = await stripe.redirectToCheckout({
      sessionId: response.sessionId,
    });
    if (!isActionCurrent()) return true;
    if (error) {
      toast.error(error.message || 'Checkout redirect failed');
      return false;
    }
    return true;
  }
  toast.error('No checkout session returned');
  return false;
}
